import { defineStore } from 'pinia';
import { ref, computed, type Ref } from 'vue';
import { ibWS } from '@ibgroup-tws-webapps/ib.utils';
import { app } from '@ibgroup-tws-webapps/ratio.ui';
import type { PnlPositionFilters, PnlPosition } from '@/types/pnl.types';
import type { AccountPnLSubscriptionResponse, Position, PositionData } from '@/types/accountPnl.types';

export const usePositions = defineStore('positions', () => {
  const accountId = ref<string>('');
  const filters = ref<PnlPositionFilters | undefined>(undefined);
  const positionsMap = ref<Map<number, PnlPosition>>(new Map());
  const currency = ref('');
  const isLoading = ref(false);
  const error = ref<Error | null>(null);
  const warning = ref('');

  let subscriptionObservable: { unsubscribe: () => void } | undefined;
  let currentSubscriptionId: string | null = null;
  let initialized = false;
  let subscribing = false;

  function initService() {
    if (initialized) return;
    try {
      ibWS.init('forecast.trader2');
      initialized = true;
    } catch {
      // Already initialized elsewhere — that's fine
      initialized = true;
    }
  }

  function mapPosition(raw: Position): PnlPosition {
    return {
      conid: raw.conid,
      position: raw.position,
      daily_pnl: raw.daily_pnl ?? '0',
      market_value: raw.market_value ?? '0',
      put_or_call: raw.put_or_call ? parseInt(raw.put_or_call, 10) : 0,
      ec_descr_1: raw.ec_descr_1 ?? '',
      ec_descr_2: raw.ec_descr_2 ?? '',
      unrealized_pnl: raw.unrealized_pnl ?? '0',
      avg_cost: raw.avg_cost,
      cost_basis: raw.cost_basis,
      realized_pnl: raw.realized_pnl,
      market_price: raw.market_price,
    };
  }

  function handleMessage(message: unknown) {
    try {
      if (!message || typeof message !== 'object' || !('payload' in message)) return;

      const payload = (message as { payload: unknown }).payload as Partial<AccountPnLSubscriptionResponse>;

      if (payload.currency) {
        currency.value = payload.currency;
      }

      if (payload.positions && Array.isArray(payload.positions)) {
        payload.positions.forEach((raw: Position) => {
          positionsMap.value.set(raw.conid, mapPosition(raw));
        });
        // Trigger reactivity
        positionsMap.value = new Map(positionsMap.value);
      }

      isLoading.value = false;
      error.value = null;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
    }
  }

  function unsubscribe() {
    if (currentSubscriptionId) {
      try {
        ibWS.sendMessage({
          message: {
            command: 'SEND',
            headers: {
              destination: '/account/unsubscribe',
              'x-target': 'pnl',
              id: currentSubscriptionId,
            },
          },
        });
      } catch {
        // Best-effort server unsubscribe
      }
    }
    if (subscriptionObservable) {
      subscriptionObservable.unsubscribe();
      subscriptionObservable = undefined;
    }
    currentSubscriptionId = null;
  }

  async function subscribe(id: string, positionFilters?: PnlPositionFilters) {
    initService();
    unsubscribe();

    if (!id) {
      positionsMap.value = new Map();
      currency.value = '';
      isLoading.value = false;
      return;
    }

    if (subscribing) return;
    subscribing = true;

    isLoading.value = true;
    error.value = null;

    try {
      const webAppId = await ibWS.getWebAppId();
      const requestId = `account-pnl-${id}-${webAppId}`;
      currentSubscriptionId = requestId;

      const fieldMask = positionFilters?.field_mask ?? [
        'daily_pnl',
        'market_value',
        'unrealized_pnl',
        // `avg_cost` and `cost_basis` are prefetched so the order ticket overlay can reuse this PnL subscription instead of unsub/resubscribing.
        'avg_cost',
        'cost_basis',
        'ec_descr_1',
        'ec_descr_2',
        'put_or_call',
        'event_product_conid',
        'security_exchange',
        'opposite_conid',
        'strike_label',
        'underlying_name',
      ];

      ibWS.sendMessage({
        message: {
          command: 'SEND',
          headers: {
            destination: '/account/subscribe',
            'x-target': 'pnl',
            id: requestId,
          },
          body: {
            account_id: id,
            filters: {
              field_mask: fieldMask,
              include_total: false,
              only_event_contracts: positionFilters?.only_event_contracts ?? true,
            },
          },
        },
      });

      subscriptionObservable = ibWS.receive(requestId)?.subscribe({
        next: (msg: unknown) => handleMessage(msg),
        error: (err: Error) => {
          error.value = err;
          isLoading.value = false;
        },
      });
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      isLoading.value = false;
    } finally {
      subscribing = false;
    }
  }

  const setAccountId = (id: string, positionFilters?: PnlPositionFilters) => {
    if (id === accountId.value && positionFilters === filters.value) return;
    accountId.value = id;
    filters.value = positionFilters;
    positionsMap.value = new Map();
    subscribe(id, positionFilters);
  };

  const positions = computed(() => Array.from(positionsMap.value.values()));

  const topPositions = computed(() => {
    return positions.value
      .slice()
      .sort((a, b) => {
        const aValue = parseFloat(a.market_value) || 0;
        const bValue = parseFloat(b.market_value) || 0;
        return bValue - aValue;
      })
      .slice(0, 3);
  });

  const getPositionsByConid = (conid: number): PnlPosition[] => {
    const pos = positionsMap.value.get(conid);
    return pos ? [pos] : [];
  };

  const getPositionByConid = (conids: number | number[]): PnlPosition[] => {
    const conidArray = Array.isArray(conids) ? conids : [conids];
    return conidArray.reduce<PnlPosition[]>((acc, conid) => {
      const pos = positionsMap.value.get(conid);
      if (pos) acc.push(pos);
      return acc;
    }, []);
  };

  const hasPosition = (conid: number): boolean => {
    const pos = positionsMap.value.get(conid);
    return !!(pos && parseInt(pos.position, 10) !== 0);
  };

  const positionDataMap = computed<Record<number, PositionData>>(() => {
    const map: Record<number, PositionData> = {};
    positionsMap.value.forEach((pos, conid) => {
      map[conid] = {
        conid,
        position: parseInt(pos.position, 10) || 0,
        marketValue: parseFloat(pos.market_value) || 0,
        dailyPnl: parseFloat(pos.daily_pnl) || 0,
        unrealizedPnl: parseFloat(pos.unrealized_pnl) || 0,
        marketPrice: parseFloat(pos.market_price ?? '0') || 0,
      };
    });
    return map;
  });

  const refreshPositions = () => {
    // WebSocket is real-time; no-op
  };

  function suspend() {
    unsubscribe();
    positionsMap.value = new Map();
  }

  function resume() {
    if (accountId.value) {
      subscribe(accountId.value, filters.value);
    }
  }

  // Listen for session suspend/resume from native bridge (singleton — one listener per store)
  const bridge = app.getBridge?.();
  bridge?.onMessage((msg) => {
    if (msg.action === 'session_suspended') {
      suspend();
    } else if (msg.action === 'session_resumed') {
      resume();
    }
  });

  return {
    accountId: accountId as Ref<string>,
    filters: filters as Ref<PnlPositionFilters | undefined>,

    positions,
    positionDataMap,
    topPositions,
    currency,
    isLoading,
    error,
    warning,

    setAccountId,
    getPositionsByConid,
    getPositionByConid,
    hasPosition,
    refreshPositions,
    suspend,
    resume,
  };
});
